import {
  CreateFieldInput,
  CreateFieldSelect,
  CreateFormActions,
  CreateNextSteps,
  PageTitle
} from '../../components.js';
import { AppConfig } from '../../config.js';
import { Page } from '../base.js';

export class AdminsCreate extends Page {
  static roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' }
  ];

  static nextSteps = [
    { icon: 'mail', label: 'Send an email invitation' },
    { icon: 'users', label: 'Manage their role' },
    { icon: 'settings', label: 'Review account access' },
    { icon: 'chart', label: 'Track admin activity' }
  ];

  static css = `
    .create-status {
      margin-top: 16px;
      color: #2a2f36;
      font-size: 14px;
      line-height: 1.5;
    }

    .create-status.error {
      color: #b42318;
    }

    .create-status.success {
      color: #2f6f3e;
    }

    .create-status-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  `;

  static timlComponents = {
    CreateFieldInput,
    CreateFieldSelect,
    CreateFormActions,
    CreateNextSteps,
    PageTitle
  };

  static timl = `
    .auth-create-page.admins-create-page
      <PageTitle title="Invite Admin">
      .auth-create-content
        p.auth-create-description Invite someone to help manage your Instant Suite account.
        .create-page-grid
          form#admins-create-form.create-form-card[method="POST"]
            .create-form-body
              <CreateFieldInput label="Email address" name="email" type="email" placeholder="e.g. teammate@example.com" hint="We’ll send an invitation to this address.">
              <CreateFieldSelect label="Role" name="role" options="{{ Routing.routeObject.roleOptions }}" value="admin" hint="Controls what this administrator can access.">
              div#create-status.create-status
            <CreateFormActions submitLabel="Invite admin" cancelHref="/admins">
          <CreateNextSteps items="{{ Routing.routeObject.nextSteps }}">
  `;

  static javascript() {
    const form = document.querySelector('#admins-create-form');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();

      await this.submitCreateForm(form);
    });
  }

  static async submitCreateForm(form) {
    const submitButton = form.querySelector('button[type="submit"], button');
    const statusEl = form.querySelector('#create-status');
    const emailInput = form.querySelector('[name="email"]');
    const roleInput = form.querySelector('[name="role"]');
    const email = (emailInput?.value || '').trim();
    const role = (roleInput?.value || '').trim() || 'admin';

    this.clearCreateErrors(form);

    if (!email) {
      this.setCreateStatus(statusEl, 'Please enter an email address.', 'error');
      if (emailInput) emailInput.style.borderColor = 'red';
      return;
    }

    submitButton.disabled = true;
    this.setCreateStatus(statusEl, 'Please wait while we create the admin profile.', 'loading');

    try {
      this.setCreateStatus(statusEl, 'Sending invitation email...', 'loading');
      const invitation = await this.inviteAdmin(AppConfig.suiteAuthAppId || '1', { email, role });
      const invitedUserId = invitation?.user?.id || '';

      if (!invitedUserId) {
        throw new Error('The invitation was sent, but no user id was returned.');
      }

      this.setCreateStatus(statusEl, 'Invitation sent. Creating the account profile...', 'loading');
      const createResponse = await this.createAdminProfile({ email, role, userId: invitedUserId });
      const admin = createResponse?.data || null;

      if (!admin?.id) {
        throw new Error('The admin profile was created, but no profile id was returned.');
      }

      API.invalidateCache('profiles');
      API.invalidateCache('admins');

      const nextUrl = `/${Translations.langCode}/admins/${admin.id}`;
      TIML.handleRedirect(nextUrl);
    } catch (error) {
      console.error('Admin invite error:', error);
      this.setCreateStatus(statusEl, error.message || 'We could not create the admin right now.', 'error');
    } finally {
      TIML.remove('.loader');
      submitButton.disabled = false;
    }
  }

  static clearCreateErrors(form) {
    form.querySelectorAll('input, select, textarea').forEach((field) => {
      field.style.borderColor = '';
      field.style.backgroundColor = '';
    });
  }

  static setCreateStatus(statusEl, message, state = '') {
    if (!statusEl) return;

    statusEl.className = 'create-status';
    statusEl.innerHTML = '';

    if (state === 'error') {
      statusEl.classList.add('error');
      statusEl.textContent = message;
      return;
    }

    if (state === 'success') {
      statusEl.classList.add('success');
      statusEl.textContent = message;
      return;
    }

    if (state === 'loading') {
      const row = document.createElement('div');
      row.className = 'create-status-row';
      row.innerHTML = `<div class="loader"></div><span>${message}</span>`;
      statusEl.appendChild(row);
      return;
    }

    statusEl.textContent = message;
  }

  static async createAdminProfile(payload) {
    return API.post(API.resourceUrl('admins'), payload);
  }

  static async inviteAdmin(authAppId, payload) {
    await Auth.ensureSuiteBootstrap();
    const accessToken = await Auth.getValidAccessToken();
    const response = await fetch(`${AppConfig.authServiceApiBaseUrl}/${Translations.langCode}/${authAppId}/invite-admin`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email: payload.email,
        role: payload.role,
        confirmEmailUrl
      })
    });

    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || `Invite admin request failed (${response.status}).`);
    }

    return data;
  }
}
